import React, { useCallback, useEffect } from 'react';
import { AddToCalendar } from '@shared/components/AddToCalendar';
import { getEventCalendarDates } from '@shared/components/AddToCalendar/AddToCalendar.utils';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGetGuestSiteScheduleLazyQuery } from '../../../widgets/ScheduleList/ScheduleList.graphql.generated';
import { addressStr } from '@shared/utils/formatting/location.formats';
import { useGuestGuideTelemetry } from '../GuestGuideTelemetry';
import { useTranslation } from '@shared/core';
import { GuestGuideActionButton } from '../GuestGuideActionButton';
import { Calendar } from '@withjoy/joykit/icons';

export const AddToCalendarAction: React.FC = () => {
  const { eventHandle, eventInfo } = useEventInfo();
  const telemetry = useGuestGuideTelemetry();
  const { tq } = useTranslation('guestGuide');
  const [querySchedule, { data, loading }] = useGetGuestSiteScheduleLazyQuery({
    batchMode: 'fast',
    ssr: false,
  });

  useEffect(() => {
    if (eventHandle) {
      querySchedule({
        variables: {
          eventHandle,
          isAdmin: false,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventHandle]);

  const finalizedDate = eventInfo?.finalizedDate?.dateString ? eventInfo.finalizedDate.dateString : undefined;
  const events = data?.eventByName?.info.schedule.formattedSchedules.flatMap(day => day.items);
  const mainEvent = events?.find(ev => ev.isMain);

  // Shared with the save the dates and the Schedule page so every surface resolves the same entry:
  // the main item's own times when it has them, an all-day entry on the event date otherwise.
  const calendarDates = getEventCalendarDates({ mainScheduleItem: mainEvent, eventDate: finalizedDate });

  const describedByMainEvent = !!mainEvent?.startTime?.timestamp;
  const title = (describedByMainEvent ? mainEvent?.name : eventInfo?.eventDisplayName) || undefined;
  const location = (describedByMainEvent ? mainEvent?.location : eventInfo?.locationInfo && addressStr(eventInfo.locationInfo)) || undefined;
  const description = (describedByMainEvent ? mainEvent?.description : undefined) || undefined;

  const handleClick = useCallback(() => {
    telemetry.clickAddToCalendar();
  }, [telemetry]);

  if (loading || !calendarDates) {
    return null;
  }

  return (
    <AddToCalendar
      start={calendarDates.start}
      end={calendarDates.end}
      allDay={calendarDates.allDay}
      location={location}
      description={description}
      title={title}
      scheduleItemId={describedByMainEvent ? mainEvent?.id : undefined}
    >
      <GuestGuideActionButton
        icon={<Calendar size={32} />}
        onClick={handleClick}
      >
        {tq`Add to Calendar`}
      </GuestGuideActionButton>
    </AddToCalendar>
  );
};
